/**
 * THE CURRENT SESSION, ASSEMBLED FROM SIGN-IN AND PROVED ON EVERY RELOAD.
 *
 * WHY A SNAPSHOT REMAINS. `GET /v1/user/profile` and the branch exchange now
 * carry the company and the chosen branch when the server sends them
 * (24-09-2026). A branch may also carry `modules`, bands of permission
 * strings (02-10-2026 17:34 UTC). Since 30-09-2026 the flattened strings are
 * matched as-is; an omitted `modules`, or an empty one, grants no protected
 * work. The module name is not copied onto `session.modules`. Vocabulary
 * confirmation remains in B5.
 * The snapshot beside the token keeps those missing facts, and it is the
 * fallback when a profile read omits the company or the branch. A 401 clears
 * both. The server still supplies no expiry; when it does, the snapshot can go.
 *
 * Nothing stored here is healthcare data: the operator's own name and id, their
 * company and the shop they are standing in. It dies with the tab.
 */
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { z } from "zod/mini";
import { grantsFromModules } from "@/api/modules-permissions";
import { isAppError } from "@/api/problems";
import { clearToken, readToken, writeToken } from "@/api/token";
import { MODULE_IDS } from "@/domain/modules";
import { viewFromScreenLayout } from "@/lib/view-mode";
import { readProfile, type GrantedBranch, type LoginPayload, type UserProfile } from "./api";

/** Empty string is not a name. Absent and blank both read as "not stated". */
function textOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

const roleSchema = z.pipe(
  z.object({
    roleCode: z.string(),
    /** English, as the server sent it. Absent when that name was not sent. */
    roleNameEn: z.optional(z.nullable(z.string())),
    /**
     * Snapshots from before both names were stored. The English name lived
     * here, with Chinese only as a fallback inside it. Still read, so a tab
     * signed in before the change still opens.
     */
    roleName: z.optional(z.string()),
    roleNameZh: z.optional(z.nullable(z.string())),
  }),
  z.transform((role) => ({
    roleCode: role.roleCode,
    roleNameEn: textOrNull(role.roleNameEn ?? role.roleName),
    roleNameZh: textOrNull(role.roleNameZh),
  })),
);

export const sessionSchema = z.object({
  staffId: z.string(),
  staffName: z.string(),
  staffNameZh: z.nullable(z.string()),
  registeredPharmacist: z.boolean(),
  tenantId: z.string(),
  companyName: z.string(),
  companyNameZh: z.nullable(z.string()),
  storeId: z.string(),
  branchName: z.string(),
  branchNameZh: z.nullable(z.string()),

  /**
   * The account's roles AT THE SIGNED-IN BRANCH. An array because the contract
   * makes it one; the specs hold one group per branch per account (GAPS.md B5).
   */
  roles: z.array(roleSchema),

  /**
   * Branch grants as supplied, including an explicitly empty list. Missing is
   * still recorded separately, but neither missing nor empty grants protected
   * work. Known codes match exactly; no role name supplies a missing grant.
   */
  permissions: z.array(z.string()),
  permissionsSupplied: z.boolean(),

  /** The account's own language, per Rachel 18-09-2026. Raw, as the server states it. */
  displayLang: z.nullable(z.string()),

  /**
   * THE LAYOUT IS THE PERSON'S, and as of 21-09-2026 the contract says so:
   * `UserObj.screenLayout`, written by `PUT /v1/user/preference`. It was a
   * documented default here until then, listed beside `edition` and `modules`
   * as something nobody had stated.
   *
   * It is the reader's rather than the branch's or the terminal's, which is a
   * reversal in both repos and has a `DECISIONS.md` entry. Held by the
   * MACHINE, one pharmacy gets two vocabularies for the same list — "press
   * down then Enter" true at one screen and wrong at the next — failing
   * whoever moved, which in a shop of six covering three counters is
   * everybody. Held by the BRANCH, one manager answers on a details form for
   * five colleagues who were not asked, and a locum working two shops is
   * handed two platforms. The ACCOUNT is the thing that travels.
   */
  view: z.catch(z.enum(["browser", "grid"]), "browser"),

  /**
   * Not stated by the contract, so each is the documented default rather than a
   * guess dressed as a fact. `modules` empty is "the branch's enrolment is not
   * known" and `full` is the recorded temporary value (GAPS.md §E). `expiresAt`
   * null is "the server did not say" — absent, not zero.
   */
  modules: z.array(z.enum(MODULE_IDS)),
  edition: z.catch(z.enum(["full", "simple"]), "full"),
  expiresAt: z.nullable(z.string()),
});

export type Session = z.infer<typeof sessionSchema>;

/* ── assembling one ──────────────────────────────────────────────────────── */

function branchOf(payload: LoginPayload, branchCode: string): GrantedBranch | undefined {
  return payload.branches?.find((branch) => branch.branchCode === branchCode);
}

function rolesOf(branch: GrantedBranch | undefined): Session["roles"] {
  return (branch?.roles ?? []).map((role) => ({
    roleCode: role.roleCode ?? "",
    // Both names, unpicked. The screen chooses with the account's displayLang.
    roleNameEn: textOrNull(role.roleNameEn),
    roleNameZh: textOrNull(role.roleNameZh),
  }));
}

/**
 * The login payload plus the branch that was chosen, as the shape screens read.
 * When the branch exchange named a company or a branch, those win. Pure, so it
 * is tested without a network.
 */
export function buildSession(
  payload: LoginPayload,
  branchCode: string,
  tenantId: string,
  chosen?: { company?: LoginPayload["company"]; branch?: GrantedBranch },
): Session {
  const branch = chosen?.branch ?? branchOf(payload, branchCode);
  const company = chosen?.company ?? payload.company;
  const grants = grantsFromModules(branch?.modules);
  return {
    staffId: payload.staffId,
    staffName: textOrNull(payload.staffNameEn) ?? payload.staffId,
    staffNameZh: textOrNull(payload.staffNameZh),
    registeredPharmacist: payload.isPharmacist,
    tenantId,
    companyName: textOrNull(company?.companyNameEn) ?? tenantId,
    companyNameZh: textOrNull(company?.companyNameZh),
    storeId: branch?.branchCode ?? branchCode,
    branchName: textOrNull(branch?.branchNameEn) ?? branchCode,
    branchNameZh: textOrNull(branch?.branchNameZh),
    roles: rolesOf(branch),
    permissions: grants.permissions,
    permissionsSupplied: grants.permissionsSupplied,
    displayLang: textOrNull(payload.displayLang),
    view: viewFromScreenLayout(payload.screenLayout),
    modules: [],
    edition: "full",
    expiresAt: null,
  };
}

/**
 * The account's own fields, re-read from `/v1/user/profile`, plus the company
 * and the chosen branch when this read carried them. Anything it omits is kept.
 */
export function withProfile(session: Session, profile: UserProfile): Session {
  const company = profile.company;
  const branch = profile.branch;
  return {
    ...session,
    staffId: profile.staffId,
    staffName: textOrNull(profile.staffNameEn) ?? profile.staffId,
    staffNameZh: textOrNull(profile.staffNameZh),
    registeredPharmacist: profile.isPharmacist,
    displayLang: textOrNull(profile.displayLang),
    // Re-read with the rest of the account, so a layout changed at another
    // terminal arrives here on the next profile fetch rather than at the next
    // sign-in. The pickers write the snapshot themselves for the press you
    // just made; this is how the OTHER machine finds out.
    view: viewFromScreenLayout(profile.screenLayout),
    ...(company
      ? {
          companyName: textOrNull(company.companyNameEn) ?? session.companyName,
          companyNameZh: textOrNull(company.companyNameZh) ?? session.companyNameZh,
        }
      : {}),
    ...(branch
      ? {
          storeId: branch.branchCode,
          branchName: textOrNull(branch.branchNameEn) ?? session.branchName,
          branchNameZh: textOrNull(branch.branchNameZh) ?? session.branchNameZh,
          ...(branch.roles ? { roles: rolesOf(branch) } : {}),
          ...grantsFromModules(branch.modules),
        }
      : {}),
  };
}

/* ── the snapshot beside the token ───────────────────────────────────────── */

const SNAPSHOT_KEY = "pharmos.auth.session";
let snapshotMemory: Session | null = null;

function store(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  snapshotMemory = session;
  try {
    store()?.setItem(SNAPSHOT_KEY, JSON.stringify(session));
  } catch {
    // Memory only: the session lasts for this page.
  }
}

export function readStoredSession(): Session | null {
  const storage = store();
  if (!storage) return snapshotMemory;
  try {
    const raw = storage.getItem(SNAPSHOT_KEY);
    if (raw === null) return null;
    const parsed = sessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return snapshotMemory;
  }
}

/** Forgets the token and the snapshot together: one without the other is a broken state. */
export function clearSession(): void {
  snapshotMemory = null;
  clearToken();
  try {
    store()?.removeItem(SNAPSHOT_KEY);
  } catch {
    // Memory only.
  }
}

/* ── ending one ──────────────────────────────────────────────────────────── */

export const sessionKeys = {
  all: ["auth"] as const,
  current: () => [...sessionKeys.all, "session"] as const,
};

/**
 * Bumped when a session ends. A response that left under an earlier value is
 * the previous operator's, and writing it back would sign them in again.
 */
let sessionEpoch = 0;

export function currentSessionEpoch(): number {
  return sessionEpoch;
}

/**
 * How the app sends the operator to sign-in. Registered once, from `main`.
 * Cleanup itself does not know the router: logout navigates, a 401 invalidates
 * so the guard can keep the page they were on.
 */
let onSessionEnded: (() => void) | undefined;

export function setSessionEndedHandler(handler: () => void): void {
  onSessionEnded = handler;
}

function isSessionQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === sessionKeys.all[0];
}

/** Drops every cached query except the session. The caller decides what the session becomes. */
export function dropOperatorQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => !isSessionQuery(query.queryKey) });
}

/**
 * ONE WAY A SESSION ENDS, used by sign-out and by a protected 401.
 *
 * The token, the snapshot and the previous operator's cached rows go together.
 * In-flight queries are cancelled first: a cancel puts an in-flight query back
 * to the data it held, which is that operator's, so the drop comes after.
 * `sessionEpoch` covers the response that resolves anyway.
 *
 * `notify` is false for an explicit sign-out, which navigates itself, and for
 * the start of a new sign-in, which is already on that screen. A 401 leaves it
 * on so the guard can run.
 *
 * `keepSessionFetch` is set when this is called from inside the session query:
 * cancelling that fetch from within it rejects the load with a cancellation
 * instead of the `null` the guard redirects on.
 */
export function endOperatorSession(
  queryClient: QueryClient,
  options?: { notify?: boolean; keepSessionFetch?: boolean },
): void {
  sessionEpoch += 1;
  clearSession();
  void queryClient.cancelQueries({
    predicate: (query) => !options?.keepSessionFetch || !isSessionQuery(query.queryKey),
  });
  queryClient.setQueryData(sessionKeys.current(), null);
  dropOperatorQueries(queryClient);
  if (options?.notify !== false) {
    const notify = onSessionEnded;
    if (notify) queueMicrotask(notify);
  }
}

/**
 * A 401 from the query cache. A sign-in refusal and an interim token have no
 * active session, and the sign-in screen is the one dealing with them — ending
 * the session here would throw away the recovery it is in the middle of.
 */
export function noteUnauthenticated(queryClient: QueryClient): void {
  if (readToken()?.stage !== "active") return;
  endOperatorSession(queryClient);
}

/* ── the query every reader goes through ─────────────────────────────────── */

/** Resolves to the session, or null when nobody is signed in. Any other failure is thrown. */
export const sessionQuery = queryOptions({
  queryKey: sessionKeys.current(),
  queryFn: async ({ client }): Promise<Session | null> => {
    const epoch = sessionEpoch;
    const token = readToken();
    // An interim token is a sign-in half done: it opens the branch step, nothing else.
    if (!token || token.stage !== "active") return null;

    const stored = readStoredSession();
    if (!stored) {
      clearSession();
      return null;
    }

    try {
      const profile = await readProfile();
      // Logged out, or a 401 already ended it, while this read was in flight.
      if (epoch !== sessionEpoch || readToken()?.stage !== "active") return null;
      if (profile.authToken) writeToken(profile.authToken, "active");
      return withProfile(stored, profile);
    } catch (error) {
      if (isAppError(error) && error.kind === "unauthenticated") {
        if (epoch === sessionEpoch && readToken()?.stage === "active") {
          endOperatorSession(client, { keepSessionFetch: true });
        }
        return null;
      }
      throw error;
    }
  },
  staleTime: 60_000,
});

export function useSession(): Session | null {
  return useQuery(sessionQuery).data ?? null;
}
